import React, { useState, useEffect } from 'react';

interface DiagnosticInfo {
  environment: {
    REACT_APP_BACKEND_URL: string;
    REACT_APP_API_BASE_URL: string;
    NODE_ENV: string;
  };
  apiTest: {
    baseUrl: string;
    endpoint: string;
    status: 'pending' | 'success' | 'error';
    response?: any;
    error?: string;
  };
  imageTest: {
    testUrls: Array<{
      input: string;
      output: string;
      status: 'pending' | 'success' | 'error';
    }>;
  };
}

export const ImageDiagnostic: React.FC = () => {
  const [diagnostic, setDiagnostic] = useState<DiagnosticInfo>({
    environment: {
      REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 'não definido',
      REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'não definido',
      NODE_ENV: process.env.NODE_ENV || 'não definido'
    },
    apiTest: {
      baseUrl: '',
      endpoint: '',
      status: 'pending'
    },
    imageTest: {
      testUrls: []
    }
  });

  useEffect(() => {
    runDiagnostic();
  }, []);

  const runDiagnostic = async () => {
    console.log('🔍 Iniciando diagnóstico de imagens...');
    
    // Testar configuração de ambiente
    const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
    const rawApiBaseEnv = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    const apiBase = rawApiBaseEnv || (rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`);
    
    // Garantir que o endpoint tenha /api no caminho
    const endpoint = apiBase.endsWith('/api') ? `${apiBase}/admin/imoveis/pendentes` : `${apiBase}/api/admin/imoveis/pendentes`;
    
    setDiagnostic(prev => ({
      ...prev,
      apiTest: {
        ...prev.apiTest,
        baseUrl: apiBase,
        endpoint: endpoint,
        status: 'pending'
      }
    }));

    // Testar API
    try {
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📡 Resposta da API:', data);
        
        // Verificar se as mídias estão sendo retornadas
        if (data.data && Array.isArray(data.data)) {
          console.log('🔍 Análise detalhada das mídias:');
          data.data.forEach((imovel: any, index: number) => {
            console.log(`🏠 Imóvel ${index + 1} - Mídias:`, {
              id: imovel.id,
              titulo: imovel.titulo,
              midias: imovel.midias ? imovel.midias.length : 0,
              midiasData: imovel.midias,
              hasMidias: !!imovel.midias,
              midiasType: typeof imovel.midias,
              allKeys: Object.keys(imovel)
            });
          });
        }
        
        setDiagnostic(prev => ({
          ...prev,
          apiTest: {
            ...prev.apiTest,
            status: 'success',
            response: data
          }
        }));
        
        // Testar URLs de imagens dos dados reais
        if (data.success && Array.isArray(data.data)) {
          // Se não há mídias, tentar buscar um imóvel específico por ID
          if (data.data.length > 0 && (!data.data[0].midias || data.data[0].midias.length === 0)) {
            console.log('🔍 Nenhuma mídia encontrada, testando busca individual...');
            const firstImovel = data.data[0];
            try {
              const individualResponse = await fetch(`${apiBase}/imoveis/${firstImovel.id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (individualResponse.ok) {
                const individualData = await individualResponse.json();
                console.log('🔍 Resposta individual do imóvel:', individualData);
                if (individualData.data && individualData.data.midias) {
                  console.log('✅ Mídias encontradas na busca individual:', individualData.data.midias);
                }
              }
            } catch (error) {
              console.log('❌ Erro na busca individual:', error);
            }
          }
          
          const testUrls = data.data
            .filter((imovel: any) => imovel.midias && imovel.midias.length > 0)
            .slice(0, 3) // Testar apenas os primeiros 3
            .map((imovel: any) => ({
              input: imovel.midias[0].url,
              output: getImageUrl(imovel.midias[0].url),
              status: 'pending' as const
            }));

          setDiagnostic(prev => ({
            ...prev,
            imageTest: {
              testUrls
            }
          }));

          // Testar carregamento das imagens
          for (let i = 0; i < testUrls.length; i++) {
            const testUrl = testUrls[i];
            
            // Primeiro testar com fetch para verificar se a URL responde
            console.log('🔍 Testando URL:', testUrl.output);
            fetch(testUrl.output, { method: 'HEAD' })
              .then(response => {
                console.log('📡 Resposta fetch:', response.status, response.statusText);
                if (response.ok) {
                  // Se fetch funcionou, tentar carregar a imagem
                  const img = new Image();
                  img.onload = () => {
                    console.log('✅ Imagem carregada com sucesso:', testUrl.output);
                    setDiagnostic(prev => ({
                      ...prev,
                      imageTest: {
                        testUrls: prev.imageTest.testUrls.map((url, index) => 
                          index === i ? { ...url, status: 'success' as const } : url
                        )
                      }
                    }));
                  };
                  img.onerror = (error) => {
                    console.log('❌ Erro ao carregar imagem (CORS?):', testUrl.output, error);
                    // Se erro de CORS, tentar rota de fallback
                    const fallbackUrl = testUrl.output.replace('/api/uploads/', '/uploads/');
                    console.log('🔄 Tentando fallback:', fallbackUrl);
                    
                    const fallbackImg = new Image();
                    fallbackImg.onload = () => {
                      console.log('✅ Imagem carregada via fallback:', fallbackUrl);
                      setDiagnostic(prev => ({
                        ...prev,
                        imageTest: {
                          testUrls: prev.imageTest.testUrls.map((url, index) => 
                            index === i ? { ...url, status: 'success' as const, output: fallbackUrl } : url
                          )
                        }
                      }));
                    };
                    fallbackImg.onerror = () => {
                      console.log('❌ Fallback também falhou:', fallbackUrl);
                      setDiagnostic(prev => ({
                        ...prev,
                        imageTest: {
                          testUrls: prev.imageTest.testUrls.map((url, index) => 
                            index === i ? { ...url, status: 'error' as const } : url
                          )
                        }
                      }));
                    };
                    fallbackImg.src = fallbackUrl;
                  };
                  img.src = testUrl.output;
                } else {
                  // Se fetch falhou, tentar rota de fallback
                  const fallbackUrl = testUrl.output.replace('/api/uploads/', '/uploads/');
                  fetch(fallbackUrl, { method: 'HEAD' })
                    .then(fallbackResponse => {
                      if (fallbackResponse.ok) {
                        const img = new Image();
                        img.onload = () => {
                          setDiagnostic(prev => ({
                            ...prev,
                            imageTest: {
                              testUrls: prev.imageTest.testUrls.map((url, index) => 
                                index === i ? { ...url, status: 'success' as const, output: fallbackUrl } : url
                              )
                            }
                          }));
                        };
                        img.onerror = () => {
                          setDiagnostic(prev => ({
                            ...prev,
                            imageTest: {
                              testUrls: prev.imageTest.testUrls.map((url, index) => 
                                index === i ? { ...url, status: 'error' as const } : url
                              )
                            }
                          }));
                        };
                        img.src = fallbackUrl;
                      } else {
                        setDiagnostic(prev => ({
                          ...prev,
                          imageTest: {
                            testUrls: prev.imageTest.testUrls.map((url, index) => 
                              index === i ? { ...url, status: 'error' as const } : url
                            )
                          }
                        }));
                      }
                    })
                    .catch(() => {
                      setDiagnostic(prev => ({
                        ...prev,
                        imageTest: {
                          testUrls: prev.imageTest.testUrls.map((url, index) => 
                            index === i ? { ...url, status: 'error' as const } : url
                          )
                        }
                      }));
                    });
                }
              })
              .catch(() => {
                setDiagnostic(prev => ({
                  ...prev,
                  imageTest: {
                    testUrls: prev.imageTest.testUrls.map((url, index) => 
                      index === i ? { ...url, status: 'error' as const } : url
                    )
                  }
                }));
              });
          }
        }
      } else {
        setDiagnostic(prev => ({
          ...prev,
          apiTest: {
            ...prev.apiTest,
            status: 'error',
            error: `HTTP ${response.status}: ${response.statusText}`
          }
        }));
      }
    } catch (error: any) {
      setDiagnostic(prev => ({
        ...prev,
        apiTest: {
          ...prev.apiTest,
          status: 'error',
          error: error.message
        }
      }));
    }
  };

  const getImageUrl = (midiaUrl: string): string => {
    if (!midiaUrl) return '';

    const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') || 'http://localhost:3005';
    
    // Normalizar caminho de mídia para sempre usar /api/uploads/
    const rawPath = midiaUrl.startsWith('/') ? midiaUrl : `/${midiaUrl}`;
    const idx = rawPath.toLowerCase().indexOf('/uploads');
    const uploadsPath = idx >= 0 ? rawPath.substring(idx) : rawPath;
    
    // Construir URL usando sempre /api/uploads/ (caminho correto)
    return `${rawBackend}/api${uploadsPath}`;
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', fontSize: '14px' }}>
      <h2>🔍 Diagnóstico de Imagens</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>📋 Configuração de Ambiente</h3>
        <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', color: '#ff0000' }}>
          {JSON.stringify(diagnostic.environment, null, 2)}
        </pre>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>🌐 Teste da API</h3>
        <div>
          <strong>Base URL:</strong> <span style={{ color: '#ff0000' }}>{diagnostic.apiTest.baseUrl}</span><br/>
          <strong>Endpoint:</strong> <span style={{ color: '#ff0000' }}>{diagnostic.apiTest.endpoint}</span><br/>
          <strong>Status:</strong> 
          <span style={{ 
            color: diagnostic.apiTest.status === 'success' ? 'green' : 
                   diagnostic.apiTest.status === 'error' ? 'red' : 'orange' 
          }}>
            {diagnostic.apiTest.status}
          </span>
        </div>
        
        {diagnostic.apiTest.error && (
          <div style={{ color: 'red', marginTop: '10px' }}>
            <strong>Erro:</strong> {diagnostic.apiTest.error}
          </div>
        )}
        
        {diagnostic.apiTest.response && (
          <div style={{ marginTop: '10px' }}>
            <strong>Resposta:</strong>
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', maxHeight: '200px', overflow: 'auto', color: '#ff0000' }}>
              {JSON.stringify(diagnostic.apiTest.response, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>🖼️ Teste de URLs de Imagens</h3>
        {diagnostic.imageTest.testUrls.length === 0 ? (
          <div>Nenhuma imagem para testar</div>
        ) : (
          <div>
            {diagnostic.imageTest.testUrls.map((testUrl, index) => (
              <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div><strong>URL Original:</strong> <span style={{ color: '#ff0000' }}>{testUrl.input}</span></div>
                <div><strong>URL Processada:</strong> <span style={{ color: '#ff0000' }}>{testUrl.output}</span></div>
                <div><strong>Status:</strong> 
                  <span style={{ 
                    color: testUrl.status === 'success' ? 'green' : 
                           testUrl.status === 'error' ? 'red' : 'orange' 
                  }}>
                    {testUrl.status}
                  </span>
                </div>
                {testUrl.status === 'success' && (
                  <img 
                    src={testUrl.output} 
                    alt="Teste" 
                    style={{ maxWidth: '200px', maxHeight: '150px', marginTop: '10px' }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.border = '2px solid red';
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={runDiagnostic} style={{ padding: '10px 20px', fontSize: '16px' }}>
        🔄 Executar Diagnóstico Novamente
      </button>
    </div>
  );
};
